import { useState, type FormEvent } from 'react'

import { ApiError, useAuth } from '@forbiz/web-kit'
import type {
  CompanyPartnerResponse,
  CompanyPhraseResponse,
  OccasionType,
  SenderPresetResponse,
} from '@forbiz/contracts'

import { companyPartnersApi } from '../lib/companyPartners.js'
import { companyPhrasesApi } from '../lib/companyPhrases.js'
import { OCCASION_LABEL, SYSTEM_PHRASES } from '../lib/phrases.js'
import { senderPresetsApi } from '../lib/senderPresets.js'
import { useAsync } from '../lib/useAsync.js'
import styles from '../app.module.css'

/**
 * 빠른 주문 설정 (BIZ-43·BIZ-48). 두 섹션:
 *  1) 발신자 프리셋 — 회사 공식 발신자.
 *  2) 자주 쓰는 문구(리본·카드) — 시스템 상용구(조회 전용) + 회사 등록 커스텀.
 * 기업 관리자만 관리(담당자는 주문서에서 고르기만). 서버가 최종 판정하지만 화면도 관리자만.
 */
export function QuickOrderSettingsPage() {
  const { user } = useAuth()
  if (user?.role !== 'COMPANY_ADMIN') {
    return (
      <div>
        <h1 className={styles.title}>주문 설정</h1>
        <p className={styles.empty}>발신자·문구는 기업 관리자만 관리할 수 있습니다.</p>
      </div>
    )
  }
  return (
    <div>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>주문 설정</h1>
          <p className={styles.subtitle}>
            회사 공식 발신자·자주 쓰는 문구·거래처를 등록해두면, 주문서에서 타이핑 없이 골라 씁니다.
          </p>
        </div>
      </div>
      <SenderSection />
      <PhraseSection />
      <PartnerSection />
    </div>
  )
}

// ── 발신자 프리셋 ───────────────────────────────────────────
function SenderSection() {
  const [reloadKey, setReloadKey] = useState(0)
  const { data, loading, error } = useAsync(() => senderPresetsApi.list(), [reloadKey])
  const reload = () => setReloadKey((k) => k + 1)
  const presets = data?.items ?? []

  const [editing, setEditing] = useState<SenderPresetResponse | 'new' | null>(null)
  const [senderName, setSenderName] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState('')
  const [rowError, setRowError] = useState('')

  function openNew() {
    setEditing('new')
    setSenderName('')
    setIsDefault(false)
    setFormError('')
  }
  function openEdit(p: SenderPresetResponse) {
    setEditing(p)
    setSenderName(p.senderName)
    setIsDefault(p.isDefault)
    setFormError('')
    setRowError('')
  }
  function close() {
    setEditing(null)
    setFormError('')
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFormError('')
    try {
      const body = { senderName: senderName.trim(), isDefault }
      if (editing && editing !== 'new') await senderPresetsApi.update(editing.id, body)
      else await senderPresetsApi.create(body)
      close()
      reload()
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : '저장하지 못했습니다')
    } finally {
      setBusy(false)
    }
  }
  async function makeDefault(p: SenderPresetResponse) {
    setRowError('')
    try {
      await senderPresetsApi.update(p.id, { isDefault: true })
      reload()
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : '변경하지 못했습니다')
    }
  }
  async function onDelete(p: SenderPresetResponse) {
    if (!window.confirm(`'${p.senderName}' 발신자를 삭제할까요?`)) return
    setRowError('')
    try {
      await senderPresetsApi.remove(p.id)
      if (editing && editing !== 'new' && editing.id === p.id) close()
      reload()
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : '삭제하지 못했습니다')
    }
  }

  return (
    <section className={styles.settingSection}>
      <div className={styles.settingHead}>
        <span className={styles.settingTitle}>발신자 (보내는 사람)</span>
        <button data-variant="secondary" onClick={openNew} disabled={editing === 'new'}>
          ＋ 추가
        </button>
      </div>

      {editing ? (
        <form className={styles.settingEditor} onSubmit={onSubmit}>
          <div className={styles.editorTitle}>{editing === 'new' ? '발신자 추가' : '발신자 수정'}</div>
          {formError ? <div className={styles.error}>{formError}</div> : null}
          <label className={styles.field}>
            <span className={styles.label}>발신자 (보내는 사람, 리본 아랫줄에 찍힘, 30자 이내)</span>
            <input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              maxLength={30}
              placeholder="(주)포비즈 대표이사 홍길동"
              disabled={busy}
            />
          </label>
          <label className={styles.checkboxField}>
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} disabled={busy} />
            <span>기본값으로 지정 (주문서에 자동 입력)</span>
          </label>
          <div className={styles.actions}>
            <button type="button" data-variant="secondary" onClick={close} disabled={busy}>
              취소
            </button>
            <button type="submit" data-variant="primary" disabled={busy || senderName.trim() === ''}>
              {busy ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      ) : null}

      {rowError ? <div className={styles.error}>{rowError}</div> : null}

      {error ? (
        <div className={styles.error}>{error}</div>
      ) : loading ? (
        <p className={styles.muted}>불러오는 중…</p>
      ) : presets.length === 0 && editing !== 'new' ? (
        <p className={styles.muted}>등록된 발신자가 없습니다. ‘＋ 추가’로 등록하세요.</p>
      ) : (
        <div className={styles.presetList}>
          {presets.map((p) => (
            <div key={p.id} className={styles.presetRow}>
              <div className={styles.presetInfo}>
                <div className={styles.presetLabel}>
                  {p.isDefault ? <span className={styles.presetDefault}>기본</span> : null}
                  {p.senderName}
                </div>
              </div>
              <div className={styles.presetActions}>
                {!p.isDefault ? (
                  <button data-variant="secondary" onClick={() => void makeDefault(p)}>
                    기본으로
                  </button>
                ) : null}
                <button data-variant="secondary" onClick={() => openEdit(p)}>
                  수정
                </button>
                <button data-variant="danger" onClick={() => void onDelete(p)}>
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── 자주 쓰는 문구 (리본·카드) ──────────────────────────────
const OCCASIONS: OccasionType[] = ['FUNERAL', 'CELEBRATION', 'GIFT']

function PhraseSection() {
  const [reloadKey, setReloadKey] = useState(0)
  const { data, loading, error } = useAsync(() => companyPhrasesApi.list(), [reloadKey])
  const reload = () => setReloadKey((k) => k + 1)
  const custom = data?.items ?? []
  const systemCount = OCCASIONS.reduce((n, o) => n + SYSTEM_PHRASES[o].length, 0)

  const [showSystem, setShowSystem] = useState(false)
  const [editing, setEditing] = useState<CompanyPhraseResponse | 'new' | null>(null)
  const [occasion, setOccasion] = useState<OccasionType>('FUNERAL')
  const [phrase, setPhrase] = useState('')
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState('')
  const [rowError, setRowError] = useState('')

  function openNew() {
    setEditing('new')
    setOccasion('FUNERAL')
    setPhrase('')
    setFormError('')
  }
  function openEdit(p: CompanyPhraseResponse) {
    setEditing(p)
    setOccasion(p.occasionType)
    setPhrase(p.phrase)
    setFormError('')
    setRowError('')
  }
  function close() {
    setEditing(null)
    setFormError('')
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFormError('')
    try {
      if (editing && editing !== 'new') await companyPhrasesApi.update(editing.id, { phrase: phrase.trim() })
      else await companyPhrasesApi.create({ occasionType: occasion, phrase: phrase.trim() })
      close()
      reload()
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : '저장하지 못했습니다')
    } finally {
      setBusy(false)
    }
  }
  async function onDelete(p: CompanyPhraseResponse) {
    if (!window.confirm('이 문구를 삭제할까요?')) return
    setRowError('')
    try {
      await companyPhrasesApi.remove(p.id)
      if (editing && editing !== 'new' && editing.id === p.id) close()
      reload()
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : '삭제하지 못했습니다')
    }
  }

  const isCard = occasion === 'GIFT'

  return (
    <section className={styles.settingSection}>
      <div className={styles.settingHead}>
        <span className={styles.settingTitle}>자주 쓰는 문구 (리본·카드)</span>
        <button data-variant="secondary" onClick={openNew} disabled={editing === 'new'}>
          ＋ 추가
        </button>
      </div>

      {editing ? (
        <form className={styles.settingEditor} onSubmit={onSubmit}>
          <div className={styles.editorTitle}>{editing === 'new' ? '문구 추가' : '문구 수정'}</div>
          {formError ? <div className={styles.error}>{formError}</div> : null}
          {editing === 'new' ? (
            <div className={styles.field}>
              <span className={styles.label}>유형</span>
              <div className={styles.segmented} role="radiogroup" aria-label="유형">
                {OCCASIONS.map((o) => (
                  <button
                    key={o}
                    type="button"
                    role="radio"
                    aria-checked={occasion === o}
                    className={`${styles.segment} ${occasion === o ? styles.segmentOn : ''}`}
                    onClick={() => setOccasion(o)}
                    disabled={busy}
                  >
                    {OCCASION_LABEL[o]}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <label className={styles.field}>
            <span className={styles.label}>{isCard ? '카드 문구 (100자·3줄 이내)' : '리본 문구 (30자 이내)'}</span>
            {isCard ? (
              <textarea value={phrase} onChange={(e) => setPhrase(e.target.value)} rows={3} maxLength={100} disabled={busy} />
            ) : (
              <input value={phrase} onChange={(e) => setPhrase(e.target.value)} maxLength={30} disabled={busy} />
            )}
          </label>
          <div className={styles.actions}>
            <button type="button" data-variant="secondary" onClick={close} disabled={busy}>
              취소
            </button>
            <button type="submit" data-variant="primary" disabled={busy || phrase.trim() === ''}>
              {busy ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      ) : null}

      {rowError ? <div className={styles.error}>{rowError}</div> : null}

      {error ? <div className={styles.error}>{error}</div> : null}

      {/* 회사가 등록한 문구만 기본 노출 — 이게 관리 대상. */}
      <div className={styles.presetList}>
        {OCCASIONS.flatMap((occ) =>
          custom
            .filter((c) => c.occasionType === occ)
            .map((p) => (
              <div key={p.id} className={styles.presetRow}>
                <div className={styles.presetInfo}>
                  <div className={styles.presetLabel}>
                    <span className={`${styles.occ} ${occBadge(occ)}`}>{OCCASION_LABEL[occ]}</span>
                    {p.phrase}
                  </div>
                </div>
                <div className={styles.presetActions}>
                  <button data-variant="secondary" onClick={() => openEdit(p)}>
                    수정
                  </button>
                  <button data-variant="danger" onClick={() => void onDelete(p)}>
                    삭제
                  </button>
                </div>
              </div>
            )),
        )}
        {!loading && custom.length === 0 && editing !== 'new' ? (
          <p className={styles.muted}>등록한 문구가 없어요. ‘＋ 추가’로 자주 쓰는 문구를 저장해두세요.</p>
        ) : null}
      </div>

      {/* 기본 제공 문구는 읽기 전용 참고 — 접어두고 개수만 보여준다. */}
      <button
        type="button"
        className={styles.disclosure}
        aria-expanded={showSystem}
        onClick={() => setShowSystem((v) => !v)}
      >
        <span>기본 제공 문구 {systemCount}개</span>
        <span className={styles.disclosureAction}>{showSystem ? '접기' : '보기'}</span>
      </button>
      {showSystem ? (
        <div className={styles.systemList}>
          {OCCASIONS.flatMap((occ) =>
            SYSTEM_PHRASES[occ].map((ph) => (
              <div key={`sys-${occ}-${ph}`} className={styles.systemRow}>
                <span className={`${styles.occ} ${occBadge(occ)}`}>{OCCASION_LABEL[occ]}</span>
                <span className={styles.systemText}>{ph}</span>
              </div>
            )),
          )}
        </div>
      ) : null}
      {loading ? <p className={styles.muted}>불러오는 중…</p> : null}
    </section>
  )
}

function occBadge(occ: OccasionType): string | undefined {
  return occ === 'FUNERAL' ? styles.occFuneral : occ === 'CELEBRATION' ? styles.occCelebrate : styles.occGift
}

// ── 거래처 ──────────────────────────────────────────────────
function PartnerSection() {
  const [reloadKey, setReloadKey] = useState(0)
  const { data, loading, error } = useAsync(() => companyPartnersApi.list(), [reloadKey])
  const reload = () => setReloadKey((k) => k + 1)
  const partners = data?.items ?? []

  const [editing, setEditing] = useState<CompanyPartnerResponse | 'new' | null>(null)
  const [name, setName] = useState('')
  const [memo, setMemo] = useState('')
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState('')
  const [rowError, setRowError] = useState('')

  function openNew() {
    setEditing('new')
    setName('')
    setMemo('')
    setFormError('')
  }
  function openEdit(p: CompanyPartnerResponse) {
    setEditing(p)
    setName(p.name)
    setMemo(p.memo ?? '')
    setFormError('')
    setRowError('')
  }
  function close() {
    setEditing(null)
    setFormError('')
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFormError('')
    try {
      const trimmedMemo = memo.trim()
      if (editing && editing !== 'new') {
        await companyPartnersApi.update(editing.id, { name: name.trim(), memo: trimmedMemo || null })
      } else {
        await companyPartnersApi.create({ name: name.trim(), ...(trimmedMemo ? { memo: trimmedMemo } : {}) })
      }
      close()
      reload()
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : '저장하지 못했습니다')
    } finally {
      setBusy(false)
    }
  }
  async function onDelete(p: CompanyPartnerResponse) {
    if (!window.confirm(`'${p.name}' 거래처를 삭제할까요?`)) return
    setRowError('')
    try {
      await companyPartnersApi.remove(p.id)
      if (editing && editing !== 'new' && editing.id === p.id) close()
      reload()
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : '삭제하지 못했습니다')
    }
  }

  return (
    <section className={styles.settingSection}>
      <div className={styles.settingHead}>
        <span className={styles.settingTitle}>거래처</span>
        <button data-variant="secondary" onClick={openNew} disabled={editing === 'new'}>
          ＋ 추가
        </button>
      </div>

      {editing ? (
        <form className={styles.settingEditor} onSubmit={onSubmit}>
          <div className={styles.editorTitle}>{editing === 'new' ? '거래처 추가' : '거래처 수정'}</div>
          {formError ? <div className={styles.error}>{formError}</div> : null}
          <label className={styles.field}>
            <span className={styles.label}>거래처명 (100자 이내)</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="(주)하늘꽃상조 / 서울추모공원 / ○○병원 장례식장"
              disabled={busy}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>메모 (선택, 200자 이내)</span>
            <input
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              maxLength={200}
              placeholder="담당자·비고 등"
              disabled={busy}
            />
          </label>
          <div className={styles.actions}>
            <button type="button" data-variant="secondary" onClick={close} disabled={busy}>
              취소
            </button>
            <button type="submit" data-variant="primary" disabled={busy || name.trim() === ''}>
              {busy ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      ) : null}

      {rowError ? <div className={styles.error}>{rowError}</div> : null}

      {error ? (
        <div className={styles.error}>{error}</div>
      ) : loading ? (
        <p className={styles.muted}>불러오는 중…</p>
      ) : partners.length === 0 && editing !== 'new' ? (
        <p className={styles.muted}>등록된 거래처가 없습니다. ‘＋ 추가’로 등록하세요.</p>
      ) : (
        <div className={styles.presetList}>
          {partners.map((p) => (
            <div key={p.id} className={styles.presetRow}>
              <div className={styles.presetInfo}>
                <div className={styles.presetLabel}>{p.name}</div>
                {p.memo ? <div className={styles.presetSender}>{p.memo}</div> : null}
              </div>
              <div className={styles.presetActions}>
                <button data-variant="secondary" onClick={() => openEdit(p)}>
                  수정
                </button>
                <button data-variant="danger" onClick={() => void onDelete(p)}>
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
